import { useCallback, useRef, useState } from "react";

/**
 * A form that writes to the API.
 *
 * The three things every one of them has to get right, in one place: the button
 * is disabled while the request is in flight, a second submit while the first is
 * running is dropped rather than queued, and the error is kept so the screen can
 * print what the API actually said instead of clearing the form and leaving the
 * user to guess.
 *
 * Dropping the second submit matters more than it sounds. Every write behind
 * these forms creates a record — a contact, a property, a job, a task — and none
 * of them is idempotent. A double-click on a slow connection is the ordinary way
 * a customer ends up in the book twice.
 */
export interface Submitter {
  readonly busy: boolean;
  readonly error: unknown;
  /** Runs `work`, holding `busy` and capturing anything it throws. */
  readonly submit: (work: () => Promise<void>) => Promise<void>;
  readonly reset: () => void;
}

export function useSubmit(): Submitter {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  /**
   * The guard is a ref, not the `busy` state.
   *
   * `busy` in this closure is the value from the render that created the
   * handler, and React has not necessarily re-rendered by the time a second
   * click lands — so reading it would let the duplicate through, which is
   * precisely the case being guarded against. A ref is current immediately.
   */
  const running = useRef(false);

  const submit = useCallback(
    async (work: () => Promise<void>): Promise<void> => {
      if (running.current) return;
      running.current = true;
      setBusy(true);
      setError(null);
      try {
        await work();
      } catch (cause: unknown) {
        setError(cause);
      } finally {
        running.current = false;
        setBusy(false);
      }
    },
    [],
  );

  const reset = useCallback(() => {
    setError(null);
  }, []);

  return { busy, error, submit, reset };
}
